import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { formatZAR, serviceIcon, serviceLabel, STATUS_LABEL } from "@/lib/constants";
import { AlertCircle, Plus, Wrench, Car, TrendingUp, Users, MapPin, Power, ShieldCheck, Bell, RefreshCw, BadgeCheck, Clock, Send } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const { user, refreshUser } = useAuth();
  if (!user) return null;

  if (user.role === "client") return <ClientDashboard user={user} />;
  if (user.role === "mechanic") return <MechanicDashboard user={user} refreshUser={refreshUser} />;
  return <AdminDashboard user={user} />;
}

function Header({ title, subtitle, accent }) {
  return (
    <div className="px-6 pt-8 pb-4">
      <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#3CFF7E] font-display mb-2">
        {accent}
      </div>
      <h1 className="font-display text-4xl font-black tracking-tight leading-[0.95]">{title}</h1>
      {subtitle && <p className="text-[#9CA3AF] text-sm mt-2">{subtitle}</p>}
    </div>
  );
}

// ===== Client =====
function ClientDashboard({ user }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  // Shared client location — read from localStorage on mount, updated by LocationCard.
  const [clientLoc, setClientLoc] = useState(() => {
    try {
      const raw = localStorage.getItem("afr_client_last_location");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/requests");
      setRequests(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { if (user.isApproved) load(); else setLoading(false); }, [user.isApproved]);

  // Pending approval gate — clients now require admin approval (mirrors mechanic flow)
  if (!user.isApproved) {
    return (
      <div>
        <Header accent="DRIVER" title={`Hi, ${user.name.split(" ")[0]}.`} subtitle="Welcome to AutoFix Rescue." />
        <div className="px-6 mt-2">
          <div className="border-2 border-[#E6FF3C] bg-[#E6FF3C]/5 rounded-md p-6" data-testid="client-pending-approval">
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#E6FF3C] font-display mb-2">
              Pending Review
            </div>
            <h2 className="font-display text-2xl font-black text-white">Waiting for approval</h2>
            <p className="text-sm text-[#9CA3AF] mt-2">
              Your account is under review by our team. You&apos;ll be able to request rescues once approved.
            </p>
          </div>
          <p className="text-xs text-[#9CA3AF] mt-4">
            Questions? Reach us at <span className="text-white">hello@autofixrescue.co.za</span>.
          </p>
        </div>
      </div>
    );
  }

  const active = requests.find((r) => ["pending", "accepted", "in_progress"].includes(r.status));
  // Notification count = active jobs the client should be watching (accepted / arrived / in_progress)
  const unreadCount = requests.filter((r) => ["accepted", "arrived", "in_progress"].includes(r.status)).length;

  return (
    <div>
      <ClientHeaderCard user={user} unreadCount={unreadCount} />
      <LocationCard onLocation={setClientLoc} />

      {/* Big CTA */}
      <div className="px-6 py-4">
        <button
          onClick={() => navigate("/app/request/new")}
          data-testid="client-sos-btn"
          className="w-full relative bg-[#0B1E5B] text-white rounded-md p-6 text-left active:scale-[0.99] transition-transform overflow-hidden"
        >
          <div className="absolute -right-6 -top-6 w-32 h-32 bg-[#3CFF7E] rounded-full opacity-20" />
          <div className="absolute right-6 top-6">
            <div className="relative h-10 w-10">
              <span className="pulse-ring" />
              <div className="h-10 w-10 bg-[#3CFF7E] rounded-full flex items-center justify-center relative">
                <AlertCircle className="text-white" size={18} />
              </div>
            </div>
          </div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#3CFF7E] mb-2">
            EMERGENCY
          </div>
          <div className="font-display text-2xl font-black">Request Rescue</div>
          <div className="text-[#9CA3AF] text-sm mt-1">Breakdown · Towing · Diagnostics</div>
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 px-6">
        <QuickCard to="/app/vehicles" icon={<Car size={18} />} label="My Vehicles" testid="quick-vehicles-btn" />
        <NearbyMechanicsCard loc={clientLoc} />
      </div>

      {/* Active request */}
      {active && (
        <div className="px-6 mt-6">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF] font-display mb-2">
            Active Request
          </div>
          <Link to={`/app/request/${active.id}`} className="block border-2 border-[#3CFF7E] rounded-md p-4" data-testid="active-request-card">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl">{serviceIcon(active.serviceType)}</div>
                <div className="font-display font-bold mt-1">{serviceLabel(active.serviceType)}</div>
                <div className="text-sm text-[#9CA3AF]">{active.description || "No details"}</div>
              </div>
              <span className={`status-pill status-${active.status}`}>{STATUS_LABEL[active.status]}</span>
            </div>
          </Link>
        </div>
      )}

      {/* Recent */}
      <div className="px-6 mt-6">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF] font-display">
            Recent Requests
          </div>
          <Link to="/app/requests" className="text-xs font-bold text-[#3CFF7E]" data-testid="view-all-requests">
            VIEW ALL →
          </Link>
        </div>
        {loading ? (
          <div className="text-sm text-[#9CA3AF]">Loading...</div>
        ) : requests.length === 0 ? (
          <div className="text-sm text-[#9CA3AF] border border-dashed border-white/20 rounded-md p-6 text-center">
            No requests yet. Tap "Request Rescue" to get started.
          </div>
        ) : (
          <div className="space-y-2">
            {requests.slice(0, 3).map((r) => (
              <Link key={r.id} to={`/app/request/${r.id}`} className="block border border-white/10 rounded-md p-3 hover:border-white transition-colors" data-testid={`request-row-${r.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="text-xl">{serviceIcon(r.serviceType)}</div>
                    <div>
                      <div className="font-display font-bold text-sm">{serviceLabel(r.serviceType)}</div>
                      <div className="text-xs text-[#9CA3AF]">{new Date(r.created_at).toLocaleString()}</div>
                    </div>
                  </div>
                  <span className={`status-pill status-${r.status}`}>{STATUS_LABEL[r.status]}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function QuickCard({ to, icon, label, testid }) {
  return (
    <Link to={to} data-testid={testid} className="border border-white/10 rounded-md p-4 hover:border-white transition-colors">
      <div className="text-[#3CFF7E] mb-2">{icon}</div>
      <div className="font-display font-bold text-sm">{label}</div>
    </Link>
  );
}

// Same wrapper as QuickCard — only the inner content changes to show live counts.
function NearbyMechanicsCard({ loc }) {
  const [state, setState] = useState({ loading: true, count: 0, closestKm: null });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!loc || loc.lat == null || loc.lng == null) {
        if (!cancelled) setState({ loading: false, count: 0, closestKm: null });
        return;
      }
      setState((s) => ({ ...s, loading: true }));
      try {
        const { data } = await api.get("/mechanics/nearby", { params: { lat: loc.lat, lng: loc.lng } });
        if (cancelled) return;
        const closest = data.length ? data[0].distance_km : null;
        setState({ loading: false, count: data.length, closestKm: closest });
      } catch {
        if (!cancelled) setState({ loading: false, count: 0, closestKm: null });
      }
    };
    load();
    return () => { cancelled = true; };
  }, [loc?.lat, loc?.lng]);

  return (
    <Link
      to="/app/map"
      data-testid="quick-map-btn"
      className="border border-white/10 rounded-md p-4 hover:border-white transition-colors"
    >
      <div className="text-[#3CFF7E] mb-2"><MapPin size={18} /></div>
      <div className="font-display font-bold text-sm" data-testid="nearby-mechanics-label">Nearby Mechanics</div>
      <div className="mt-1 text-[11px] text-[#9CA3AF] leading-snug" data-testid="nearby-mechanics-count">
        {state.loading
          ? "Loading…"
          : `${state.count} ${state.count === 1 ? "mechanic" : "mechanics"} online`}
      </div>
      <div className="text-[11px] text-[#9CA3AF] leading-snug" data-testid="nearby-mechanics-closest">
        {state.loading
          ? "\u00A0"
          : state.closestKm != null
            ? `Closest mechanic: ${state.closestKm.toFixed(1)} km away`
            : "Closest mechanic: —"}
      </div>
    </Link>
  );
}

// ---------- Client home enhancements (lightweight, additive only) ----------

// Reverse-geocode lat/lng → "Suburb, City" via OpenStreetMap Nominatim (free, no key).
// On any failure the caller falls back to showing raw coords.
async function reverseGeocode(lat, lng) {
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 4500);
    const r = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&zoom=14&addressdetails=1`,
      { signal: ctrl.signal, headers: { "Accept": "application/json" } },
    );
    clearTimeout(t);
    if (!r.ok) return null;
    const data = await r.json();
    const a = data.address || {};
    const primary = a.suburb || a.neighbourhood || a.village || a.town || a.hamlet || a.city_district;
    const secondary = a.city || a.town || a.municipality || a.state;
    const parts = [primary, secondary].filter(Boolean).filter((v, i, arr) => arr.indexOf(v) === i);
    if (parts.length) return parts.slice(0, 2).join(", ");
    return data.display_name ? data.display_name.split(",").slice(0, 2).join(",").trim() : null;
  } catch {
    return null;
  }
}

function ClientHeaderCard({ user, unreadCount }) {
  const navigate = useNavigate();
  const firstName = (user.name || "").split(" ")[0] || "there";
  const greeting = (() => {
    const h = new Date().getHours();
    if (h >= 5 && h < 12) return "Good Morning";
    if (h >= 12 && h < 18) return "Good Afternoon";
    return "Good Evening";
  })();
  const memberSince = (() => {
    if (!user.created_at) return null;
    try {
      return new Date(user.created_at).toLocaleDateString("en-GB", {
        day: "numeric", month: "short", year: "numeric",
      });
    } catch { return null; }
  })();
  const isVerified = user.verificationStatus === "verified" || user.isApproved;

  return (
    <div className="px-6 pt-8 pb-2" data-testid="client-home-header">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h1
            className="font-display text-3xl font-black tracking-tight leading-[0.95] break-words"
            data-testid="client-greeting"
          >
            {greeting}, {firstName}.
          </h1>
          <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-display font-bold uppercase tracking-[0.18em] text-[#3CFF7E]" data-testid="client-availability">
            <span className="w-1.5 h-1.5 rounded-full bg-[#3CFF7E] animate-pulse" />
            24/7 Available
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate("/app/requests")}
          data-testid="client-notif-bell"
          aria-label={`Notifications (${unreadCount} unread)`}
          className="relative h-11 w-11 rounded-md border border-white/10 bg-[#111827] flex items-center justify-center text-white active:scale-95 transition-transform shrink-0"
        >
          <Bell size={18} />
          {unreadCount > 0 && (
            <span
              data-testid="client-notif-count"
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#3CFF7E] text-[#0A0A0A] text-[10px] font-display font-black flex items-center justify-center"
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      </div>

      {/* Meta row: verified badge · member since */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {isVerified && (
          <span
            data-testid="client-verified-badge"
            className="inline-flex items-center gap-1 text-[10px] font-display font-bold uppercase tracking-[0.15em] px-2 py-1 rounded-md border border-[#3CFF7E]/40 bg-[#3CFF7E]/10 text-[#3CFF7E]"
          >
            <BadgeCheck size={12} /> Verified Client
          </span>
        )}
        {memberSince && (
          <span
            data-testid="client-member-since"
            className="inline-flex items-center gap-1 text-[10px] font-display font-bold uppercase tracking-[0.15em] px-2 py-1 rounded-md border border-white/10 bg-[#111827] text-[#9CA3AF]"
          >
            <Clock size={12} /> Member since {memberSince}
          </span>
        )}
      </div>
    </div>
  );
}

function LocationCard({ onLocation }) {
  const [coords, setCoords] = useState(() => {
    // Restore last-known so the card isn't blank on first paint
    try {
      const raw = localStorage.getItem("afr_client_last_location");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const [busy, setBusy] = useState(false);

  const refresh = () => {
    if (!navigator.geolocation) {
      toast.error("GPS not available on this device.");
      return;
    }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (p) => {
        const next = {
          lat: p.coords.latitude,
          lng: p.coords.longitude,
          ts: Date.now(),
        };
        setCoords(next);
        if (onLocation) onLocation(next);
        try { localStorage.setItem("afr_client_last_location", JSON.stringify(next)); } catch { /* ignore */ }
        // Reverse-geocode in the background; fall back to coords silently on failure.
        const place = await reverseGeocode(next.lat, next.lng);
        if (place) {
          const withPlace = { ...next, place };
          setCoords(withPlace);
          try { localStorage.setItem("afr_client_last_location", JSON.stringify(withPlace)); } catch { /* ignore */ }
        }
        setBusy(false);
        toast.success("Location updated");
      },
      (err) => {
        setBusy(false);
        toast.error(err.code === 1 ? "Permission denied. Enable GPS." : "Couldn't get GPS.");
      },
      // maximumAge: 0 → force a fresh reading every time the client taps Refresh,
      // so the coords actually update after they've moved.
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  // Auto-refresh once on mount if we don't have a location yet
  useEffect(() => {
    if (!coords) refresh();
  }, []);

  const lastUpdated = coords?.ts
    ? new Date(coords.ts).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
    : null;

  return (
    <div className="px-6 pt-3" data-testid="client-location-card-wrapper">
      <div className="border border-white/10 bg-[#111827] rounded-md p-3 flex items-center gap-3" data-testid="client-location-card">
        <div className="h-9 w-9 rounded-md bg-[#3CFF7E]/10 border border-[#3CFF7E]/40 flex items-center justify-center text-[#3CFF7E] shrink-0">
          <MapPin size={16} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF] font-display">
            Current Location
          </div>
          {coords ? (
            <>
              <div className="text-[13px] text-white truncate" data-testid="client-location-coords">
                {coords.place ? coords.place : `${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)}`}
              </div>
              {lastUpdated && (
                <div className="text-[10px] text-[#6B7280]">Updated {lastUpdated}</div>
              )}
            </>
          ) : (
            <div className="text-[13px] text-[#9CA3AF]" data-testid="client-location-empty">
              {busy ? "Detecting…" : "Tap refresh to detect GPS"}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={busy}
          data-testid="client-location-refresh-btn"
          aria-label="Refresh location"
          className="h-9 px-3 rounded-md bg-[#3CFF7E] text-[#0A0A0A] text-[10px] font-display font-bold uppercase tracking-wider flex items-center gap-1 active:scale-95 disabled:opacity-60 shrink-0"
        >
          <RefreshCw size={12} className={busy ? "animate-spin" : ""} />
          {busy ? "…" : "Refresh"}
        </button>
      </div>
    </div>
  );
}

// ===== Mechanic location card =====
// Mirrors the client's LocationCard but ALSO persists lat/lng to /api/users/me
// so the mechanic remains discoverable by the existing Nearby Mechanics matching.
function MechanicLocationCard({ user, refreshUser }) {
  const initial = (() => {
    // Prefer whatever the backend already has for this mechanic; fall back to cache.
    const loc = user.location || {};
    if (loc.lat != null && loc.lng != null) return { lat: loc.lat, lng: loc.lng, ts: null };
    try {
      const raw = localStorage.getItem("afr_mechanic_last_location");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();
  const [coords, setCoords] = useState(initial);
  const [busy, setBusy] = useState(false);

  const refresh = () => {
    if (!navigator.geolocation) {
      toast.error("GPS not available on this device.");
      return;
    }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (p) => {
        const next = { lat: p.coords.latitude, lng: p.coords.longitude, ts: Date.now() };
        setCoords(next);
        try { localStorage.setItem("afr_mechanic_last_location", JSON.stringify(next)); } catch { /* ignore */ }
        // Persist to backend so Nearby Mechanics keeps working with the fresh position.
        try {
          await api.put("/users/me", { location: { lat: next.lat, lng: next.lng } });
          if (refreshUser) await refreshUser();
        } catch (e) {
          setBusy(false);
          toast.error(e.response?.data?.detail || "Failed to save location");
          return;
        }
        // Reverse-geocode in background; silent fallback to coords on failure.
        const place = await reverseGeocode(next.lat, next.lng);
        if (place) {
          const withPlace = { ...next, place };
          setCoords(withPlace);
          try { localStorage.setItem("afr_mechanic_last_location", JSON.stringify(withPlace)); } catch { /* ignore */ }
        }
        setBusy(false);
        toast.success("Location updated");
      },
      (err) => {
        setBusy(false);
        toast.error(err.code === 1 ? "Permission denied. Enable GPS." : "Couldn't get GPS.");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const lastUpdated = coords?.ts
    ? new Date(coords.ts).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
    : null;

  return (
    <div className="border border-white/10 bg-[#111827] rounded-md p-3 flex items-center gap-3" data-testid="mechanic-location-card">
      <div className="h-9 w-9 rounded-md bg-[#3CFF7E]/10 border border-[#3CFF7E]/40 flex items-center justify-center text-[#3CFF7E] shrink-0">
        <MapPin size={16} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF] font-display">
          Current Location
        </div>
        {coords ? (
          <>
            <div className="text-[13px] text-white truncate" data-testid="mechanic-location-place">
              {coords.place ? coords.place : `${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)}`}
            </div>
            {lastUpdated && (
              <div className="text-[10px] text-[#6B7280]">Updated {lastUpdated}</div>
            )}
          </>
        ) : (
          <div className="text-[13px] text-[#9CA3AF]" data-testid="mechanic-location-empty">
            {busy ? "Detecting…" : "Tap refresh to detect GPS"}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={refresh}
        disabled={busy}
        data-testid="mechanic-location-refresh-btn"
        aria-label="Refresh location"
        className="h-9 px-3 rounded-md bg-[#3CFF7E] text-[#0A0A0A] text-[10px] font-display font-bold uppercase tracking-wider flex items-center gap-1 active:scale-95 disabled:opacity-60 shrink-0"
      >
        <RefreshCw size={12} className={busy ? "animate-spin" : ""} />
        {busy ? "…" : "Refresh"}
      </button>
    </div>
  );
}

// ===== Mechanic =====
function MechanicDashboard({ user, refreshUser }) {
  const [jobs, setJobs] = useState([]);
  const [nearby, setNearby] = useState([]);
  const [available, setAvailable] = useState(!!user.availability);

  const load = async () => {
    const [a, b] = await Promise.all([api.get("/requests"), api.get("/requests/nearby").catch(() => ({ data: [] }))]);
    setJobs(a.data);
    setNearby(b.data);
  };
  useEffect(() => { load(); }, []);

  const toggleAvailability = async () => {
    const newVal = !available;
    setAvailable(newVal);
    try {
      await api.put("/users/me", { availability: newVal });
      await refreshUser();
      toast.success(newVal ? "You are ONLINE" : "You are OFFLINE");
    } catch (e) {
      setAvailable(!newVal);
      toast.error(e.response?.data?.detail || "Failed to update");
    }
  };

  const activeJobs = jobs.filter((j) => ["accepted", "in_progress"].includes(j.status));

  // Pending approval gate
  if (!user.isApproved) {
    return (
      <div>
        <Header accent="MECHANIC" title={`Hi, ${user.name.split(" ")[0]}.`} subtitle="Welcome to AutoFix Rescue." />
        <div className="px-6 mt-2">
          <div className="border-2 border-[#E6FF3C] bg-[#E6FF3C]/5 rounded-md p-6" data-testid="mechanic-pending-approval">
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#E6FF3C] font-display mb-2">
              Pending Review
            </div>
            <h2 className="font-display text-2xl font-black text-white">Waiting for approval</h2>
            <p className="text-sm text-[#9CA3AF] mt-2">
              Your mechanic account is under review by our team. You'll be able to go online and accept jobs once approved.
            </p>
          </div>
          <p className="text-xs text-[#9CA3AF] mt-4">
            Questions? Reach us at <span className="text-white">hello@autofixrescue.co.za</span>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header accent="MECHANIC" title={`Hi, ${user.name.split(" ")[0]}.`} subtitle="Your rescue dashboard." />

      <div className="px-6">
        <div className="flex items-center justify-between border border-white/10 rounded-md p-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF] font-display">
              Availability
            </div>
            <div className={`font-display text-xl font-black mt-1 ${available ? "text-[#3CFF7E]" : "text-[#9CA3AF]"}`}>
              {available ? "ONLINE" : "OFFLINE"}
            </div>
          </div>
          <button
            onClick={toggleAvailability}
            data-testid="availability-toggle"
            className={`h-12 px-6 rounded-md font-display font-bold uppercase tracking-wider text-sm flex items-center gap-2 ${
              available ? "bg-[#3CFF7E] text-[#0A0A0A]" : "bg-[#0B1E5B] text-white"
            }`}
          >
            <Power size={16} /> {available ? "Go Offline" : "Go Online"}
          </button>
        </div>
      </div>

      <div className="px-6 mt-3">
        <MechanicLocationCard user={user} refreshUser={refreshUser} />
      </div>

      <div className="px-6 mt-3">
        <Link
          to="/app/messages"
          data-testid="mech-messages-link"
          className="flex items-center justify-between border border-white/10 rounded-md p-3 hover:border-white"
        >
          <div className="flex items-center gap-2">
            <Send size={14} className="text-[#3CFF7E]" />
            <span className="font-display font-bold text-sm">Message Admin</span>
          </div>
          <span className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">Support</span>
        </Link>
      </div>

      <div className="px-6 grid grid-cols-2 gap-3 mt-4">
        <Stat label="EARNINGS" value={formatZAR(user.earnings)} yellow />
        <Stat label="RATING" value={user.rating ? `${user.rating} ★` : "—"} accent />
        <Stat label="ACTIVE JOBS" value={activeJobs.length} />
        <Stat label="NEARBY" value={nearby.length} />
      </div>

      <div className="px-6 mt-6">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF] font-display">
            Nearby Requests
          </div>
          <Link to="/app/jobs" className="text-xs font-bold text-[#3CFF7E]">VIEW ALL →</Link>
        </div>
        {nearby.length === 0 ? (
          <div className="text-sm text-[#9CA3AF] border border-dashed border-white/20 rounded-md p-6 text-center">
            No pending requests in your area.
          </div>
        ) : (
          <div className="space-y-2">
            {nearby.slice(0, 3).map((r) => (
              <Link key={r.id} to={`/app/request/${r.id}`} className="block border border-white/10 rounded-md p-3 hover:border-white" data-testid={`nearby-job-${r.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="text-xl">{serviceIcon(r.serviceType)}</div>
                    <div>
                      <div className="font-display font-bold text-sm">{serviceLabel(r.serviceType)}</div>
                      <div className="text-xs text-[#9CA3AF]">{r.clientName} · {r.distance_km < 9999 ? `${r.distance_km} km away` : "distance unknown"}</div>
                    </div>
                  </div>
                  <span className="font-display font-bold text-sm">{formatZAR(r.price)}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, accent, yellow }) {
  const base = "border rounded-md p-4";
  if (yellow) {
    return (
      <div className={`${base} border-[#E6FF3C]/40 bg-[#E6FF3C]/5`}>
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] font-display text-[#E6FF3C]">
          {label}
        </div>
        <div className="font-display text-2xl font-black mt-1 text-[#E6FF3C]">{value}</div>
      </div>
    );
  }
  if (accent) {
    return (
      <div className={`${base} border-[#3CFF7E] bg-[#3CFF7E] text-[#0A0A0A]`}>
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] font-display text-[#0A0A0A]/70">
          {label}
        </div>
        <div className="font-display text-2xl font-black mt-1">{value}</div>
      </div>
    );
  }
  return (
    <div className={`${base} border-white/10 bg-[#111827]`}>
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] font-display text-[#9CA3AF]">
        {label}
      </div>
      <div className="font-display text-2xl font-black mt-1 text-white">{value}</div>
    </div>
  );
}

// ===== Admin =====
function AdminDashboard({ user }) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/analytics").then(({ data }) => setStats(data)).catch(() => {});
  }, []);
  return (
    <div>
      <Header accent="ADMIN" title="Platform Overview" subtitle="Monitor users, jobs, and revenue." />
      {!stats ? (
        <div className="px-6 text-sm text-[#9CA3AF]">Loading...</div>
      ) : (
        <div className="px-6 grid grid-cols-2 gap-3">
          <Stat label="USERS" value={stats.totalUsers} />
          <Stat label="CLIENTS" value={stats.clients} />
          <Stat label="MECHANICS" value={stats.mechanics} />
          <Stat label="REQUESTS" value={stats.totalRequests} />
          <Stat label="PENDING" value={stats.pending} />
          <Stat label="COMPLETED" value={stats.completed} />
          <Stat label="JOB VALUE" value={formatZAR(stats.totalJobValue)} />
          <Stat label="REVENUE (15%)" value={formatZAR(stats.platformRevenue)} yellow />
        </div>
      )}
      <div className="px-6 mt-6 space-y-2">
        <Link to="/app/admin/notifications" className="block border border-[#E6FF3C]/40 bg-[#E6FF3C]/5 rounded-md p-4 hover:border-[#E6FF3C]" data-testid="admin-notif-link">
          <div className="flex items-center gap-3"><AlertCircle size={18} className="text-[#E6FF3C]" /><span className="font-display font-bold text-[#E6FF3C]">Job Alerts (WhatsApp)</span></div>
        </Link>
        <Link to="/app/admin/messages" className="block border border-white/10 rounded-md p-4 hover:border-white" data-testid="admin-messages-link">
          <div className="flex items-center gap-3"><Send size={18} className="text-[#3CFF7E]" /><span className="font-display font-bold">Message Mechanics</span></div>
        </Link>
        <Link to="/app/admin/pending" className="block border border-white/10 rounded-md p-4 hover:border-white" data-testid="admin-pending-link">
          <div className="flex items-center gap-3"><ShieldCheck size={18} className="text-[#3CFF7E]" /><span className="font-display font-bold">Pending Mechanics</span></div>
        </Link>
        <Link to="/app/admin/scheduled" className="block border border-white/10 rounded-md p-4 hover:border-white" data-testid="admin-scheduled-link">
          <div className="flex items-center gap-3"><Wrench size={18} /><span className="font-display font-bold">Scheduled Jobs</span></div>
        </Link>
        <Link to="/app/admin/users" className="block border border-white/10 rounded-md p-4 hover:border-white" data-testid="admin-users-link">
          <div className="flex items-center gap-3"><Users size={18} /><span className="font-display font-bold">All Users</span></div>
        </Link>
        <Link to="/app/admin/jobs" className="block border border-white/10 rounded-md p-4 hover:border-white" data-testid="admin-jobs-link">
          <div className="flex items-center gap-3"><Wrench size={18} /><span className="font-display font-bold">All Requests</span></div>
        </Link>
      </div>
    </div>
  );
}
